import React, { useEffect, useState } from 'react';
import './PricingPage.scss';
import Title from '../../shared/components/title/Title';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import usePaddle from '../../shared/hooks/pricing/UsePaddle';
import usePaddleProducts from '../../shared/hooks/pricing/UsePaddleProducts';
import Loader from '../../shared/components/common/loader/Loader';
import { showCheckout } from './services/PricingService';
import { AccountType } from '../../shared/types/account/AccountType';
import useChangePlan from '../../shared/hooks/pricing/UseChangePlan';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { getDiscount } from '../../shared/services/pricing/PricingService';
import { DiscountResponseData } from '../../shared/models/pricing/DiscountResponse';
import { showErrorDialog } from '../../shared/helpers/DialogHelper';
import PricingBoxV1 from './components/PricingBoxV1';
import PricingBoxV2 from './components/PricingBoxV2';
import useDataLayer, {
  IDefaultDataLayer,
  IDowngradedDataLayer,
} from '../../shared/hooks/global/UseDataLayer';
import { isDowngrade } from './helpers/PricingHelper';
import PricingBoxV3 from './components/PricingBoxV3';

const PricingPage: React.FC = () => {
  const { accountResponse, refetchData } = useAccount();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { paddle } = usePaddle();
  const { products, loading, refreshProducts } = usePaddleProducts();
  const [discount, setDiscount] = useState<DiscountResponseData | null>(null);

  const { tryChangePlan } = useChangePlan();
  const { pushDataLayer } = useDataLayer();

  const confirmChangePlan = (priceId: string, accountType: AccountType) => {
    confirmDialog({
      message: t('areYouSureChangePlan'),
      header: t('question'),
      defaultFocus: 'accept',
      acceptLabel: t('confirm'),
      accept: () => changePlan(priceId, accountType),
    });
  };

  const openCheckout = async (priceId: string, accountType: AccountType) => {
    if (!paddle || !accountResponse) {
      console.error('Billing loading failed');
      return;
    }

    if (accountResponse.accountStatus === 'PAID' && accountResponse.hasSubscription) {
      confirmChangePlan(priceId, accountType);
      return;
    }

    sendCheckoutEvent();
    showCheckout(paddle, priceId, accountResponse);
  };

  const sendDowngradeEvent = (newPlan: AccountType) => {
    if (!accountResponse) {
      console.error('Downgrade event failed');
      return;
    }

    pushDataLayer<IDowngradedDataLayer>({
      event: 'subscription_downgraded',
      email_hashed: accountResponse.email,
      new_plan: newPlan.toString(),
      user_id: accountResponse.userId.toString(),
      account_id: accountResponse.accountId.toString(),
    });
  };

  const sendCheckoutEvent = () => {
    if (!accountResponse) {
      console.error('Checkout event failed');
      return;
    }

    pushDataLayer<IDefaultDataLayer>({
      event: 'checkout_started',
      email_hashed: accountResponse.email,
      user_id: accountResponse.userId.toString(),
      account_id: accountResponse.accountId.toString(),
    });
  };

  useEffect(() => {
    getDiscount().then((discount) => {
      setDiscount(discount);
    });
  }, []);

  const changePlan = async (priceId: string, accountType: AccountType) => {
    try {
      await tryChangePlan(priceId, accountType);

      if (accountResponse && isDowngrade(accountResponse, accountType)) {
        sendDowngradeEvent(accountType);
      }
      await refreshProducts();
      refetchData();
    } catch (err) {
      showErrorDialog({
        accept: async () => {
          await refreshProducts();
          refetchData();
        },
      });
    }
  };

  if (loading || !products || !discount || !accountResponse) {
    return <Loader />;
  }

  const isNewBilling = ['v2', 'v3'].includes(accountResponse.productsVersion);
  return (
    <>
      <ConfirmDialog className="dialog-box" />
      <div className={`pricing-page page-content ${isNewBilling ? 'pricing-page--v2' : ''}`}>
        <div className="page-box" key="page-box-project">
          {/* Telefon: tytuł w górnym pasku zamiast nagłówka strony. */}
          <MobilePageHeader
            title={<span>{t('subscription')}</span>}
            actions={
              <span
                className={`pricing-page__header-limits${
                  accountResponse.createdMonitorCount > accountResponse.maxMonitorCount
                    ? ' pricing-page__header-limits--error'
                    : ''
                }`}
                title={t('currentLimits')}
              >
                {Intl.NumberFormat('pl-PL').format(accountResponse.createdMonitorCount || 0)} /{' '}
                {Intl.NumberFormat('pl-PL').format(accountResponse?.maxMonitorCount || 0)}
              </span>
            }
          />
          {!isMobile && <Title title={t('subscription')} />}

          {/* Telefon: limity są w górnym pasku. */}
          {!isMobile && (
          <div
            className={`pricing-page__limits ${
              accountResponse.createdMonitorCount > accountResponse.maxMonitorCount
                ? 'pricing-page__limits--error'
                : ''
            }`}
          >
            {t('currentLimits')}:{' '}
            {Intl.NumberFormat('pl-PL').format(accountResponse.createdMonitorCount || 0)} /{' '}
            {Intl.NumberFormat('pl-PL').format(accountResponse?.maxMonitorCount || 0)}
          </div>
          )}
          {accountResponse.productsVersion === 'v1' ? (
            <PricingBoxV1
              accountResponse={accountResponse}
              discount={discount}
              products={products}
              openCheckout={openCheckout}
            />
          ) : accountResponse.productsVersion === 'v2' ? (
            <PricingBoxV2
              accountResponse={accountResponse}
              discount={discount}
              products={products}
              openCheckout={openCheckout}
            />
          ) : (
            <PricingBoxV3
              accountResponse={accountResponse}
              discount={discount}
              products={products}
              openCheckout={openCheckout}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PricingPage;
